import * as React from 'react';
import { useEffect } from 'react';
import { MapPin, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSectionHeader,
  Input,
  PhoneInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SideDrawer,
} from '@klareospace/pharos-lib-web-utils';
import { CUSTOMER_TYPE } from '@klareospace/pharos-lib-types';
import type { Customer, CustomerWithResolvedRefs, PhoneNumber } from '@klareospace/pharos-lib-types';

import { useCustomerForm } from '../hooks/use-customer-form';
import { AddressBookSection } from './address-book-section';
import { AddressForm } from './address-form';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CustomerDrawerProps {
  customer: CustomerWithResolvedRefs | null;
  onClose: () => void;
  /** Called with the newly created customer on a successful create (not fired on edit). */
  onCreated?: (customer: Customer) => void;
  open: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CustomerDrawer({ customer, onClose, onCreated, open }: CustomerDrawerProps): React.JSX.Element {
  const { i18n, t: tCommon } = useTranslation('common');
  const { t } = useTranslation('customers');
  const isEditMode = customer !== null;

  const { form, handleContactFieldChange, isSubmitDisabled, onSubmit } = useCustomerForm({
    customer,
    onCreated,
    onSuccess: onClose,
  });

  const { reset } = form;
  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  function handleOpenChange(nextOpen: boolean): void {
    if (!nextOpen) {
      form.reset();
      onClose();
    }
  }

  const selectedType = form.watch('type');
  const isCompany = selectedType === CUSTOMER_TYPE.COMPANY;

  const title = isEditMode ? t('edit.title') : t('create.title');
  const subtitle = isEditMode ? t('edit.subtitle') : t('create.subtitle');
  const submitLabel = isEditMode ? t('edit.submit') : t('create.submit');

  const footer = (
    <>
      <Button disabled={isSubmitDisabled} onClick={form.handleSubmit(onSubmit)} type="button">
        {submitLabel}
      </Button>
      <Button onClick={(): void => handleOpenChange(false)} type="button" variant="outline">
        {t('form.cancel')}
      </Button>
    </>
  );

  return (
    <SideDrawer
      autoFocus={false}
      footer={footer}
      onOpenChange={handleOpenChange}
      open={open}
      subtitle={subtitle}
      title={title}
    >
      <Form {...form}>
        <form className="space-y-6">
          <FormSectionHeader icon={User} title={t('form.sections.personalInfo')} />

          {/* ── Row: First name + Last name ─────────────────────────── */}
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }): React.JSX.Element => (
                <FormItem>
                  <FormLabel>
                    {t('form.firstName')} <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }): React.JSX.Element => (
                <FormItem>
                  <FormLabel>
                    {t('form.lastName')} <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* ── Row: Type + Company name ────────────────────────────── */}
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="type"
              render={({ field }): React.JSX.Element => (
                <FormItem>
                  <FormLabel>
                    {t('form.typeLabel')} <span className="text-destructive">*</span>
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={CUSTOMER_TYPE.CLIENT}>{t('form.typeOptions.client')}</SelectItem>
                      <SelectItem value={CUSTOMER_TYPE.COMPANY}>{t('form.typeOptions.company')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isCompany && (
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }): React.JSX.Element => (
                  <FormItem>
                    <FormLabel>
                      {t('form.companyName')} <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          {/* ── Row: Phone + Email ──────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="phone"
              render={({ field }): React.JSX.Element => (
                <FormItem>
                  <FormLabel>
                    {t('form.phone')} <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <PhoneInput
                      countryAriaLabel={tCommon('phoneInput.countryAriaLabel')}
                      emptyText={tCommon('phoneInput.noCountryFound')}
                      locale={i18n.language}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      searchPlaceholder={tCommon('phoneInput.searchPlaceholder')}
                      value={(field.value as PhoneNumber | null) ?? null}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }): React.JSX.Element => (
                <FormItem>
                  <FormLabel>{t('form.email')}</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* ── Row: Loyalty number ─────────────────────────────────── */}
          <FormField
            control={form.control}
            name="loyaltyNumber"
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>{t('form.loyaltyNumber')}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* ── Section: Address book ──────────────────────── */}
          {customer !== null ? (
            <AddressBookSection customer={customer} form={form} />
          ) : (
            <>
              <div className="flex items-center gap-2">
                <FormSectionHeader icon={MapPin} title={t('form.sections.addressBook')} />
                <span className="text-xs text-muted-foreground">({t('form.sections.optional')})</span>
              </div>
              <AddressForm form={form} handleContactFieldChange={handleContactFieldChange} index={0} />
            </>
          )}
        </form>
      </Form>
    </SideDrawer>
  );
}
